import { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Atom, X, ArrowDown, ArrowUp, CalendarClock, Loader2, Plus } from 'lucide-react';
import { type DateRange } from 'react-day-picker';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Line, LineChart, XAxis, YAxis, CartesianGrid } from 'recharts';
import { AnimatePresence, motion } from 'framer-motion';
import { format } from 'date-fns';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { cn } from '@/lib/utils';
import { useOrganization } from '@/hooks/useOrganization';
import { toast } from '@/hooks/use-toast';
import performanceService, {
  type PerformanceRange,
  type PerformanceDataSource,
  type RubricOverviewItem,
  type RubricChangeEvent,
} from '@/lib/api/performance';
import customEvalsService from '@/lib/api/customEvals';

// ─── Chart colours ───────────────────────────────────────────────────────────

// Semantic chart palette — uses warm neutrals + muted accents
const PALETTE = [
  'hsl(var(--foreground))',
  'hsl(var(--muted-foreground))',
  'hsl(var(--border))',
  'hsl(var(--secondary-foreground))',
  'hsl(var(--muted-foreground) / 0.6)',
  'hsl(var(--foreground) / 0.7)',
  'hsl(var(--secondary-foreground) / 0.5)',
  'hsl(var(--border) / 0.8)',
];

type TrendSource = 'rubrics' | 'baseline';

/** Generic series shape used by both rubric and baseline trends. */
interface TrendSeries {
  key: string;
  name: string;
  points: { ts: string; avg_score: number }[];
}

function buildChartConfig(series: TrendSeries[]): ChartConfig {
  const config: ChartConfig = {};
  series.forEach((s, i) => {
    config[s.key] = {
      label: s.name,
      color: PALETTE[i % PALETTE.length],
    };
  });
  return config;
}

// ─── Normalise trend series into flat records for Recharts ────────────────────

interface ChartRow {
  ts: string;
  [key: string]: number | string;
}

function flattenTrendSeries(series: TrendSeries[]): ChartRow[] {
  const map = new Map<string, ChartRow>();
  for (const s of series) {
    for (const p of s.points) {
      const ts = p.ts;
      if (!map.has(ts)) map.set(ts, { ts });
      map.get(ts)![s.key] = Math.min(p.avg_score, 100);
    }
  }
  return Array.from(map.values()).sort((a, b) => a.ts.localeCompare(b.ts));
}

// ─── Date Range Selector ─────────────────────────────────────────────────────

const dateRangeOptions: { label: string; value: PerformanceRange; icon?: boolean }[] = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: '7D', value: '7d' },
  { label: '30D', value: '30d' },
  { label: 'Custom', value: 'custom', icon: true },
];

function DateRangeSelector({
  value,
  onChange,
  customRange,
  onCustomRangeChange,
}: {
  value: PerformanceRange;
  onChange: (v: PerformanceRange) => void;
  customRange: DateRange | undefined;
  onCustomRangeChange: (r: DateRange | undefined) => void;
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);

  const customLabel = customRange?.from
    ? customRange.to
      ? `${format(customRange.from, 'MMM dd')} - ${format(customRange.to, 'MMM dd, yyyy')}`
      : format(customRange.from, 'MMM dd, yyyy')
    : 'Custom';

  return (
    <div className="flex items-start overflow-hidden rounded-[4px] border border-border bg-card shrink-0">
      {dateRangeOptions.map((opt) =>
        opt.value === 'custom' ? (
          <Popover key={opt.value} open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                className={cn(
                  'h-[28px] px-3 flex items-center justify-center border-r border-border text-sm tracking-[-0.14px] transition-colors last:border-r-0 whitespace-nowrap',
                  value === 'custom'
                    ? 'bg-accent dark:bg-accent font-semibold text-foreground shadow-[0px_0px_4px_0px_rgba(0,0,0,0.04)]'
                    : 'font-medium text-muted-foreground hover:bg-accent dark:hover:bg-accent/50',
                )}
              >
                <CalendarClock size={16} className="mr-2" />
                {customLabel}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={customRange?.from}
                selected={customRange}
                onSelect={(range) => {
                  onCustomRangeChange(range);
                  if (range?.from && range?.to) {
                    onChange('custom');
                    setPopoverOpen(false);
                  }
                }}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        ) : (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={cn(
              'h-[28px] px-3 flex items-center justify-center border-r border-border text-sm tracking-[-0.14px] transition-colors last:border-r-0 whitespace-nowrap',
              value === opt.value
                ? 'bg-accent dark:bg-accent font-semibold text-foreground shadow-[0px_0px_4px_0px_rgba(0,0,0,0.04)]'
                : 'font-medium text-muted-foreground hover:bg-accent dark:hover:bg-accent/50',
            )}
          >
            {opt.label}
          </button>
        ),
      )}
    </div>
  );
}

// ─── Trend Source Toggle ─────────────────────────────────────────────────────

function TrendSourceToggle({
  value,
  onChange,
}: {
  value: TrendSource;
  onChange: (v: TrendSource) => void;
}) {
  return (
    <div className="flex items-start overflow-hidden rounded-[4px] border border-border bg-card shrink-0">
      {([
        { label: 'Rubrics', value: 'rubrics' as const },
        { label: 'Baseline', value: 'baseline' as const },
      ]).map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={cn(
            'h-[28px] px-3 flex items-center justify-center border-r border-border text-sm tracking-[-0.14px] transition-colors last:border-r-0 whitespace-nowrap',
            value === opt.value
              ? 'bg-accent dark:bg-accent font-semibold text-foreground shadow-[0px_0px_4px_0px_rgba(0,0,0,0.04)]'
              : 'font-medium text-muted-foreground hover:bg-accent dark:hover:bg-accent/50',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Data Source Toggle ─────────────────────────────────────────────────────

function DataSourceToggle({
  value,
  onChange,
}: {
  value: PerformanceDataSource;
  onChange: (v: PerformanceDataSource) => void;
}) {
  return (
    <div className="flex items-start overflow-hidden rounded-[4px] border border-border bg-card shrink-0">
      {([
        { label: 'Live', value: 'live' as const },
        { label: 'Eval', value: 'eval' as const },
      ]).map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={cn(
            'h-[28px] px-3 flex items-center justify-center border-r border-border text-sm tracking-[-0.14px] transition-colors last:border-r-0 whitespace-nowrap',
            value === opt.value
              ? 'bg-accent dark:bg-accent font-semibold text-foreground shadow-[0px_0px_4px_0px_rgba(0,0,0,0.04)]'
              : 'font-medium text-muted-foreground hover:bg-accent dark:hover:bg-accent/50',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Performance Trend Chart ─────────────────────────────────────────────────

const MAX_DEFAULT_SERIES = 6;

function PerformanceTrendChart({
  series,
  chartConfig,
  trendSource,
}: {
  series: TrendSeries[];
  chartConfig: ChartConfig;
  trendSource: TrendSource;
}) {
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);
  const data = useMemo(() => flattenTrendSeries(series), [series]);

  // Hide excess series by default when data loads
  useEffect(() => {
    if (series.length > MAX_DEFAULT_SERIES && !showAll) {
      setHiddenKeys(new Set(series.slice(MAX_DEFAULT_SERIES).map(s => s.key)));
    }
  }, [series.length, showAll]);

  const toggleSeries = (key: string) => {
    setHiddenKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const visibleSeries = series.filter(s => !hiddenKeys.has(s.key));

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-section-title text-foreground">
          {trendSource === 'rubrics' ? 'Performance Trend' : 'Baseline Score Trend'}
        </p>
        {series.length > MAX_DEFAULT_SERIES && (
          <button
            onClick={() => {
              if (showAll) {
                setHiddenKeys(new Set(series.slice(MAX_DEFAULT_SERIES).map(s => s.key)));
                setShowAll(false);
              } else {
                setHiddenKeys(new Set());
                setShowAll(true);
              }
            }}
            className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
          >
            {showAll ? `Show top ${MAX_DEFAULT_SERIES}` : `Show all ${series.length}`}
          </button>
        )}
      </div>
      <div className="p-4">
        {/* Legend — clickable to toggle series */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          {series.map((s, i) => {
            const isHidden = hiddenKeys.has(s.key);
            return (
              <button
                key={s.key}
                onClick={() => toggleSeries(s.key)}
                className={cn(
                  'flex items-center gap-1.5 text-xs font-medium transition-opacity',
                  isHidden ? 'opacity-30' : 'opacity-100'
                )}
              >
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ backgroundColor: PALETTE[i % PALETTE.length] }}
                />
                <span className="text-muted-foreground leading-4">
                  {s.name}
                </span>
              </button>
            );
          })}
        </div>

        {data.length === 0 ? (
          <div className="flex items-center justify-center h-[220px] text-sm text-muted-foreground">
            No trend data yet.
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="aspect-auto h-[220px] w-full">
            <LineChart data={data} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid
                vertical={false}
                strokeDasharray="3 3"
                className="stroke-[hsl(var(--border))]"
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tickMargin={8}
                width={40}
                domain={[0, 100]}
                tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              />
              <XAxis
                dataKey="ts"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={50}
                tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(value) => {
                  try {
                    return new Date(value).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    });
                  } catch {
                    return '--';
                  }
                }}
              />
              <ChartTooltip
                cursor={{
                  strokeWidth: 1,
                  strokeDasharray: '4 4',
                  className: 'stroke-[hsl(var(--border))]',
                }}
                content={
                  <ChartTooltipContent
                    className="w-[215px] backdrop-blur-[7px] bg-white/95/95 border border-border rounded-[6px] shadow-[0px_15px_15px_0px_rgba(0,0,0,0.09)]"
                    labelFormatter={(value) => {
                      try {
                        return new Date(value).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        });
                      } catch {
                        return 'Invalid';
                      }
                    }}
                    indicator="dot"
                  />
                }
              />
              {series.map((s, i) => {
                const color = PALETTE[i % PALETTE.length];
                const isHidden = hiddenKeys.has(s.key);
                return (
                  <Line
                    key={s.key}
                    dataKey={s.key}
                    type="monotone"
                    stroke={color}
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={isHidden ? false : { r: 3, strokeWidth: 0, fill: color }}
                    hide={isHidden}
                  />
                );
              })}
            </LineChart>
          </ChartContainer>
        )}
      </div>
    </div>
  );
}

// ─── Status Badge ────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const isActive = status === 'active';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 h-5 px-1.5 py-0.5 rounded text-xs font-medium',
        isActive
          ? 'bg-success/10 text-success'
          : 'bg-muted text-muted-foreground',
      )}
    >
      <span
        className={cn(
          'w-1 h-1 rounded-full',
          isActive ? 'bg-success' : 'bg-muted-foreground',
        )}
      />
      {isActive ? 'Active' : 'Draft'}
    </span>
  );
}

// ─── Score Badge ─────────────────────────────────────────────────────────────

function ScoreBadge({ score }: { score: number | null }) {
  if (score === null) {
    return (
      <span className="inline-flex items-center h-5 px-1.5 py-0.5 rounded bg-muted dark:bg-muted text-xs font-medium text-muted-foreground">
        —
      </span>
    );
  }
  const isGood = score >= 15;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 h-5 px-1.5 py-0.5 rounded text-xs font-medium',
        isGood
          ? 'bg-success/10 text-success'
          : 'bg-muted text-muted-foreground',
      )}
    >
      <span
        className={cn(
          'w-1 h-1 rounded-full',
          isGood ? 'bg-success' : 'bg-muted-foreground',
        )}
      />
      {score.toFixed(1)}
    </span>
  );
}

// ─── Sort helpers ────────────────────────────────────────────────────────────

type SortDir = 'asc' | 'desc' | null;
type SortField = 'name' | 'description' | 'status' | 'score7d' | 'lastModified';

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active || !dir) {
    return <ArrowDown size={14} className="text-muted-foreground opacity-40" />;
  }
  return dir === 'asc' ? (
    <ArrowUp size={14} className="text-muted-foreground" />
  ) : (
    <ArrowDown size={14} className="text-muted-foreground" />
  );
}

// ─── Rubrics Table ───────────────────────────────────────────────────────────

function RubricsTable({
  rubrics,
  selectedKey,
  onRowClick,
  emptyMessage = 'No rubrics yet.',
}: {
  rubrics: RubricOverviewItem[];
  selectedKey?: string | null;
  onRowClick?: (r: RubricOverviewItem) => void;
  emptyMessage?: string;
}) {
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : sortDir === 'desc' ? null : 'asc');
      if (sortDir === 'desc') setSortField(null);
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    if (!sortField || !sortDir) return rubrics;
    return [...rubrics].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'description':
          cmp = a.description.localeCompare(b.description);
          break;
        case 'status':
          cmp = a.status.localeCompare(b.status);
          break;
        case 'score7d':
          cmp = (a.score_7d ?? -1) - (b.score_7d ?? -1);
          break;
        case 'lastModified':
          cmp =
            new Date(a.last_modified_at ?? 0).getTime() -
            new Date(b.last_modified_at ?? 0).getTime();
          break;
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }, [rubrics, sortField, sortDir]);

  const sortableHeader = (label: string, field: SortField) => (
    <button
      className="flex items-center gap-1 hover:text-foreground dark:hover:text-heading transition-colors"
      onClick={() => handleSort(field)}
    >
      {label}
      <SortIcon active={sortField === field} dir={sortField === field ? sortDir : null} />
    </button>
  );

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-b border-border">
            <TableHead className="h-9 px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider w-10">
              #
            </TableHead>
            <TableHead className="h-9 px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {sortableHeader('Name', 'name')}
            </TableHead>
            <TableHead className="h-9 px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {sortableHeader('Description', 'description')}
            </TableHead>
            <TableHead className="h-9 px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {sortableHeader('Status', 'status')}
            </TableHead>
            <TableHead className="h-9 px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {sortableHeader('7D Score', 'score7d')}
            </TableHead>
            <TableHead className="h-9 px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {sortableHeader('Last Modified', 'lastModified')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-12 text-sm text-muted-foreground">
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            sorted.map((rubric, idx) => (
              <TableRow
                key={rubric.criterion_key}
                className={cn(
                  'h-10 border-b border-border transition-colors',
                  onRowClick && 'cursor-pointer',
                  selectedKey === rubric.criterion_key
                    ? 'bg-accent/50 dark:bg-accent/30'
                    : 'hover:bg-muted/30',
                )}
                onClick={() => onRowClick?.(rubric)}
              >
                <TableCell className="px-3 py-2 text-sm font-medium text-muted-foreground">
                  {idx + 1}
                </TableCell>
                <TableCell className="px-3 py-2 text-sm font-semibold text-foreground tracking-[-0.14px]">
                  {rubric.name}
                </TableCell>
                <TableCell className="px-3 py-2 text-sm font-medium text-muted-foreground tracking-[-0.14px] max-w-[300px] truncate">
                  {rubric.description}
                </TableCell>
                <TableCell className="px-3 py-2">
                  <StatusBadge status={rubric.status} />
                </TableCell>
                <TableCell className="px-3 py-2">
                  <ScoreBadge score={rubric.score_7d} />
                </TableCell>
                <TableCell className="px-3 py-2 text-sm font-medium text-muted-foreground tracking-[-0.14px]">
                  {rubric.last_modified_at
                    ? format(new Date(rubric.last_modified_at), 'dd MMM, h:mm a')
                    : '—'}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

// ─── Detail Panel ────────────────────────────────────────────────────────────

function RubricDetailPanel({
  orgId,
  criterionKey,
  onClose,
}: {
  orgId: string;
  criterionKey: string;
  onClose: () => void;
}) {
  const { data: detail, isLoading: isDetailLoading } = useQuery({
    queryKey: ['rubric-detail', orgId, criterionKey],
    queryFn: async () => {
      const res = await performanceService.getRubricDetail(orgId, criterionKey, { active_only: false });
      if (!res.success || !res.data) throw new Error(res.error || 'Failed to load rubric');
      return res.data;
    },
    enabled: !!orgId && !!criterionKey,
  });

  const { data: changesData } = useQuery({
    queryKey: ['rubric-changes', orgId, criterionKey],
    queryFn: async () => {
      const res = await performanceService.getRubricChanges(orgId, criterionKey, { active_only: false });
      if (!res.success || !res.data) throw new Error(res.error || 'Failed to load changes');
      return res.data;
    },
    enabled: !!orgId && !!criterionKey,
  });

  const changes: RubricChangeEvent[] = changesData?.changes ?? [];

  return (
    <motion.div
      initial={{ x: 40, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 40, opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="w-[360px] shrink-0 border-l border-border bg-white overflow-y-auto"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <span className="text-sm font-semibold text-foreground">
          Rubric Details
        </span>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
          <X size={16} />
        </Button>
      </div>

      {isDetailLoading || !detail ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Name & Description */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                <span className="text-muted-foreground text-xs">*</span>
              </div>
              <span className="text-xs font-semibold text-foreground">
                Name & Description
              </span>
            </div>
            <p className="text-sm font-semibold text-foreground mb-1">
              {detail.name}
            </p>
            <p className="text-sm text-muted-foreground leading-5">
              {detail.description}
            </p>
          </div>

          {/* Attributes */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                <span className="text-muted-foreground text-xs">*</span>
              </div>
              <span className="text-xs font-semibold text-foreground">
                Attributes
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <StatusBadge status={detail.status} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">7D Score</span>
                <ScoreBadge score={detail.score_7d} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Samples (7D)
                </span>
                <span className="text-sm text-foreground">
                  {detail.sample_count_7d}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Last Modified
                </span>
                <span className="text-sm text-foreground">
                  {detail.last_modified_at
                    ? format(new Date(detail.last_modified_at), 'dd MMM, h:mm a')
                    : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Evaluation Prompt */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                <span className="text-muted-foreground text-xs">*</span>
              </div>
              <span className="text-xs font-semibold text-foreground">
                Evaluation Prompt
              </span>
            </div>
            <p className="text-sm text-muted-foreground leading-5 whitespace-pre-wrap">
              {detail.evaluation_prompt}
            </p>
          </div>

          {/* Change History */}
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                <span className="text-muted-foreground text-xs">*</span>
              </div>
              <span className="text-xs font-semibold text-foreground">
                Change History
              </span>
            </div>
            {changes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No changes yet</p>
            ) : (
              <div className="space-y-3">
                {changes.map((ch) => (
                  <div key={ch.version_id}>
                    <p className="text-sm font-medium text-foreground">
                      {ch.change_summary}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {ch.author ?? 'System'} &bull;{' '}
                      {format(new Date(ch.timestamp), 'dd MMM, h:mm a')}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </motion.div>
  );
}

// ─── Create Rubric Dialog ────────────────────────────────────────────────────

const RUBRIC_NAME_MAX = 255;
const EVAL_PROMPT_MIN = 10;

type RubricFormStatus = 'draft' | 'active';

interface CreateRubricFormState {
  name: string;
  description: string;
  status: RubricFormStatus | '';
  evaluationPrompt: string;
}

const initialCreateRubricForm: CreateRubricFormState = {
  name: '',
  description: '',
  status: '',
  evaluationPrompt: '',
};

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 128);
}

function CreateRubricDialog({
  open,
  onOpenChange,
  organizationId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
}) {
  const queryClient = useQueryClient();

  const [form, setForm] = useState<CreateRubricFormState>(initialCreateRubricForm);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof CreateRubricFormState, string>>>({});

  // Reset form every time the dialog opens
  useEffect(() => {
    if (open) {
      setForm(initialCreateRubricForm);
      setFieldErrors({});
    }
  }, [open]);

  const validate = (): boolean => {
    const errors: Partial<Record<keyof CreateRubricFormState, string>> = {};

    const trimmedName = form.name.trim();
    if (!trimmedName) {
      errors.name = 'Rubric name is required.';
    } else if (trimmedName.length > RUBRIC_NAME_MAX) {
      errors.name = `Name must be ${RUBRIC_NAME_MAX} characters or fewer.`;
    } else if (!slugify(trimmedName)) {
      errors.name = 'Name must contain at least one alphanumeric character.';
    }

    if (!form.status) {
      errors.status = 'Please select a status.';
    }

    const trimmedPrompt = form.evaluationPrompt.trim();
    if (!trimmedPrompt) {
      errors.evaluationPrompt = 'Evaluation prompt is required.';
    } else if (trimmedPrompt.length < EVAL_PROMPT_MIN) {
      errors.evaluationPrompt = `Prompt must be at least ${EVAL_PROMPT_MIN} characters.`;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const createMutation = useMutation({
    retry: false,
    mutationFn: async (formData: CreateRubricFormState) => {
      const trimmedName = formData.name.trim();
      const key = slugify(trimmedName);

      // Step 1: Create the eval set with a draft version
      const createRes = await customEvalsService.createEvalSet({
        organization_id: organizationId,
        name: trimmedName,
        description: formData.description.trim() || undefined,
        initial_version: {
          criteria: [
            {
              key,
              name: trimmedName,
              rubric: formData.evaluationPrompt.trim(),
            },
          ],
        },
      });

      if (!createRes.success || !createRes.data) {
        throw new Error(createRes.error || 'Failed to create rubric.');
      }

      const evalSet = createRes.data;

      // Step 2: If user chose "active", publish the draft version
      if (formData.status === 'active') {
        const draftVersion = evalSet.versions?.[0];
        if (!draftVersion) {
          throw new Error('Rubric created but no version was returned. Please activate it manually.');
        }

        const publishRes = await customEvalsService.publishVersion(
          evalSet.id,
          draftVersion.id,
          { organization_id: organizationId },
        );

        if (!publishRes.success) {
          // The rubric was created as draft — surface a specific warning rather than a hard failure
          queryClient.invalidateQueries({ queryKey: ['performance-overview', organizationId] });
          throw Object.assign(
            new Error('Rubric created as draft. Failed to activate — you can update the status from the rubrics list.'),
            { isPartialSuccess: true },
          );
        }
      }

      return evalSet;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performance-overview', organizationId] });
      toast({ title: 'Rubric created', description: 'The new rubric has been created successfully.' });
      onOpenChange(false);
    },
    onError: (error: any) => {
      if (error?.isPartialSuccess) {
        toast({
          title: 'Rubric created as draft',
          description: error.message,
        });
        onOpenChange(false);
      } else {
        const msg = error instanceof Error ? error.message : '';
        const isDuplicate =
          /duplicate key|already exists|unique constraint/i.test(msg);

        if (isDuplicate) {
          setFieldErrors((prev) => ({
            ...prev,
            name: 'A rubric with this name already exists.',
          }));
        } else {
          toast({
            title: 'Error creating rubric',
            description: msg || 'Something went wrong. Please try again.',
            variant: 'destructive',
          });
        }
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    createMutation.mutate(form);
  };

  const setField = <K extends keyof CreateRubricFormState>(key: K, value: CreateRubricFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create New Rubric</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Rubric Name */}
          <div className="space-y-1.5">
            <Label htmlFor="rubric-name">Rubric Name</Label>
            <Input
              id="rubric-name"
              placeholder="Rubric"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              maxLength={RUBRIC_NAME_MAX}
              disabled={createMutation.isPending}
            />
            {fieldErrors.name && (
              <p className="text-xs text-destructive">{fieldErrors.name}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="rubric-description">Description</Label>
            <Textarea
              id="rubric-description"
              placeholder="Description"
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              rows={3}
              disabled={createMutation.isPending}
            />
          </div>

          {/* Status */}
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select
              value={form.status}
              onValueChange={(v) => setField('status', v as RubricFormStatus)}
              disabled={createMutation.isPending}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
              </SelectContent>
            </Select>
            {fieldErrors.status && (
              <p className="text-xs text-destructive">{fieldErrors.status}</p>
            )}
          </div>

          {/* Evaluation Prompt */}
          <div className="space-y-1.5">
            <Label htmlFor="rubric-prompt">Evaluation Prompt</Label>
            <Textarea
              id="rubric-prompt"
              placeholder="Rubric"
              value={form.evaluationPrompt}
              onChange={(e) => setField('evaluationPrompt', e.target.value)}
              rows={5}
              disabled={createMutation.isPending}
            />
            {fieldErrors.evaluationPrompt && (
              <p className="text-xs text-destructive">{fieldErrors.evaluationPrompt}</p>
            )}
          </div>

          {/* Submit */}
          <Button type="submit" className="w-full" disabled={createMutation.isPending}>
            {createMutation.isPending ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating...
              </span>
            ) : (
              'Create New Rubric'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

const PerformancePage: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  const [dateRange, setDateRange] = useState<PerformanceRange>('30d');
  const [customRange, setCustomRange] = useState<DateRange | undefined>(undefined);
  const [trendSource, setTrendSource] = useState<TrendSource>('rubrics');
  const [dataSource, setDataSource] = useState<PerformanceDataSource>('live');
  const [selectedCriterionKey, setSelectedCriterionKey] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const customReady = dateRange !== 'custom' || !!(customRange?.from && customRange?.to);
  const customParams = dateRange === 'custom' && customRange?.from && customRange?.to
    ? { start: customRange.from.toISOString(), end: customRange.to.toISOString() }
    : {};

  // Custom evals — always fetched for rubrics table
  const {
    data: overview,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['performance-overview', orgId, dateRange, dataSource, 'custom', customParams.start, customParams.end],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const res = await performanceService.getOverview(orgId, {
        range: dateRange,
        source: 'custom',
        data_source: dataSource,
        active_only: false,
        ...customParams,
      });
      if (!res.success || !res.data) throw new Error(res.error || 'Failed to load performance data');
      return res.data;
    },
    enabled: !!orgId && customReady,
    staleTime: 30_000,
  });

  // Baseline evals — always fetched for baseline scores table + chart toggle
  const { data: baselineOverview } = useQuery({
    queryKey: ['performance-overview', orgId, dateRange, dataSource, 'baseline', customParams.start, customParams.end],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const res = await performanceService.getOverview(orgId, {
        range: dateRange,
        source: 'baseline',
        data_source: dataSource,
        ...customParams,
      });
      if (!res.success || !res.data) throw new Error(res.error || 'Failed to load baseline data');
      return res.data;
    },
    enabled: !!orgId && customReady,
    staleTime: 30_000,
  });

  const activeSeries: TrendSeries[] =
    trendSource === 'rubrics'
      ? (overview?.trend_series ?? [])
      : (baselineOverview?.trend_series ?? []);
  const chartConfig = useMemo(() => buildChartConfig(activeSeries), [activeSeries]);

  const rubrics = overview?.rubrics ?? [];

  return (
    <>
      <Helmet>
        <title>Performance | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col h-full">
        <PageHeader title="Performance" />

        {/* Content + Detail panel side-by-side */}
        <div className="flex flex-1 min-h-0">
          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 md:px-8 pb-8">
              {isLoading ? (
                <div className="flex items-center justify-center py-24">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : error ? (
                <div className="text-center py-24">
                  <p className="text-sm text-muted-foreground">
                    Unable to load performance data. Please try again.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-6">
                  {/* Controls bar — two rows */}
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <TrendSourceToggle value={trendSource} onChange={setTrendSource} />
                        <DataSourceToggle value={dataSource} onChange={setDataSource} />
                      </div>
                      <Button
                        size="sm"
                        onClick={() => setCreateDialogOpen(true)}
                      >
                        <Plus className="h-4 w-4 mr-1.5" />
                        Create New Rubric
                      </Button>
                    </div>
                    <div className="flex">
                      <DateRangeSelector value={dateRange} onChange={setDateRange} customRange={customRange} onCustomRangeChange={setCustomRange} />
                    </div>
                  </div>

                  {/* Chart */}
                  <div className="border border-border rounded-lg p-4 bg-card shadow-card">
                    <PerformanceTrendChart
                      series={activeSeries}
                      chartConfig={chartConfig}
                      trendSource={trendSource}
                    />
                  </div>

                  {/* Table — shows based on active tab */}
                  <div>
                    {trendSource === 'rubrics' ? (
                      <>
                        <h2 className="text-section-title text-foreground mb-3">Rubrics</h2>
                        <RubricsTable
                          rubrics={rubrics}
                          selectedKey={selectedCriterionKey}
                          onRowClick={(r) => setSelectedCriterionKey(r.criterion_key)}
                        />
                      </>
                    ) : (
                      <>
                        <h2 className="text-section-title text-foreground mb-1">Baseline Scores</h2>
                        <p className="text-xs text-muted-foreground mb-3">
                          Read-only benchmark scores aggregated by evaluator from completed evaluation runs.
                        </p>
                        <RubricsTable
                          rubrics={baselineOverview?.rubrics ?? []}
                          emptyMessage="No baseline scores yet."
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
          </div>

          {/* Detail panel */}
          <AnimatePresence mode="wait">
            {selectedCriterionKey && orgId && (
              <RubricDetailPanel
                key={selectedCriterionKey}
                orgId={orgId}
                criterionKey={selectedCriterionKey}
                onClose={() => setSelectedCriterionKey(null)}
              />
            )}
          </AnimatePresence>
        </div>
      </div>

      {orgId && (
        <CreateRubricDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          organizationId={orgId}
        />
      )}
    </>
  );
};

export default PerformancePage;
